import { db, StoryEntity } from '@/shared/lib/db'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { LexicalEditor } from 'lexical'
import { nanoid } from 'nanoid'
import { useEffect, useState } from 'react'
import { KEYS } from '@/components/custom/editor/stringKeys/StringKeys'
import {
    convertLexicalToDBJson,
    setJsonScriptEditor,
} from '@/features/screenplay/components/editor/utils/JsonUtils'
import { ParaNode } from './editor/nodes/ParaNode'
import { StEditorInstancePlugin } from './editor/plugins/StEditorInstancePlugin'
import StEnterPlugin from './editor/plugins/StEnterPlugin'
import StNodeToolbarPlugin from './editor/plugins/StNodeToolbarPlugin'
import { useStEditorProvider } from './StEditorProvider'
import StAIWriterChatPlugin from './editor/plugins/StAIWriterChatPlugin'
import StParaTransformPlugin from './editor/plugins/StParaTransformPlugin'
import { useUpdateStoryMutation } from '../queries/story.queries'
import { useStoryEditor } from '../hooks/useStoryEditor'
import { toastService } from '@/shared/services/toast.service'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';

const DEFAULT_CONTENT = [
    {
        type: 'para',
        children: [{ text: 'lets begin story' }],
        id: nanoid(),
    },
]

const StoryEditor = () => {
    const { jsonScript, storyEntity } = useStoryEditor()
    const { featuresEnabled } = useStEditorProvider()
    const { mutateAsync: updateStory } = useUpdateStoryMutation()

    function onError(error: any) {
        console.error(error)
    }

    const initialConfig = {
        namespace: 'StEditor',
        onError,
        nodes: [ParaNode],
        editorState: (initialEditor: LexicalEditor) => {
            if (!jsonScript) return
            setJsonScriptEditor(initialEditor, jsonScript)
        },
    }

    if (!storyEntity || !jsonScript) return null
    return (
        <LexicalComposer key={storyEntity.id} initialConfig={initialConfig}>
            <StEditorInstancePlugin />
            <RichTextPlugin
                contentEditable={
                    <ContentEditable
                        className="flex flex-col outline-none items-center w-[8in] min-h-screen p-[1in] bg-gray-50"
                        id={KEYS.ST_CONTENT_EDITABLE}
                    />
                }
                ErrorBoundary={LexicalErrorBoundary}
            />
            <StEnterPlugin />
            <OnChangePlugin
                onChange={(editorState, editor, tags) => {
                    // Simple debounce implementation
                    const timeoutId = setTimeout(async () => {
                        const id = storyEntity?.id
                        const editorJson = editorState.toJSON()
                        const data = convertLexicalToDBJson(editorJson)
                        try {
                            if (id && data) {
                                await updateStory({ ...storyEntity, content: data, updated_at: new Date() })
                            }
                        } catch (err) {
                            console.error('failed to save content:', err)
                            toastService.error(err as string)
                        }
                    }, 1000) // 1 second debounce

                    return () => clearTimeout(timeoutId)
                }}
            />
            <HistoryPlugin />
            {featuresEnabled.nodeToolbar && <StNodeToolbarPlugin />}
            <StParaTransformPlugin />
            <StAIWriterChatPlugin />
        </LexicalComposer>
    )
}

export default StoryEditor
